import { useCallback, useEffect, useState } from 'react'
import {
  collection,
  documentId,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { db } from '../firebase'
import { isDemoMode } from '../demoMode'
import { DEMO_CLIENT_DATA } from '../demoClientData'
import { useServerCounts } from './useServerCounts'
import {
  DIRECTORY_CHUNK_SIZE,
  DIRECTORY_STATUSES,
  loadAllPages,
  sortDirectoryRows,
  toDirectoryRow,
  type DirectoryRow,
  type DirectoryServerCounts,
} from '../leads/leadDirectory'

/** Count specs for the status pills — module constant so the reference is stable. */
const STATUS_COUNT_SPECS: Record<string, readonly QueryConstraint[]> = {
  total: [],
  ...Object.fromEntries(DIRECTORY_STATUSES.map((s) => [s, [where('status', '==', s)]])),
}

export interface LeadDirectory {
  /** Every lead row loaded so far, newest activity first (slim — no interactions). */
  rows: DirectoryRow[]
  /** True once every page has been read — the loaded set IS the full set. */
  complete: boolean
  /** Server-side count() totals — the numbers a client is shown as totals. */
  server: DirectoryServerCounts
  error: Error | null
  /** Re-read rows and counts (e.g. after an import). */
  reload: () => void
}

/**
 * useLeadDirectory — the WHOLE "My Customers" list for one client (2026-10-09).
 *
 * Replaces reading `useClientData().leads` (capped at 100) on the Customers page.
 * Rows are paged by document id — not by `lastInteractionAt`, because an orderBy on a
 * field silently drops every doc missing it — in chunks of DIRECTORY_CHUNK_SIZE until
 * the collection is exhausted, then sorted newest-first in memory. Totals come from
 * count() aggregation (`useServerCounts`), so the page can say "1,585" before the
 * last row arrives. A one-shot read, not a live listener: 1,500+ docs on a listener
 * would re-deliver on every lead write; `reload()` refreshes on demand.
 */
export function useLeadDirectory(
  clientId: string | null | undefined,
  /** The client's past-customer setting (pastCustomerAfterDaysOf(client)) — every row's standing uses it. */
  pastCustomerAfterDays?: number,
): LeadDirectory {
  const [rows, setRows] = useState<DirectoryRow[]>([])
  const [complete, setComplete] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const reload = useCallback(() => setReloadKey((k) => k + 1), [])
  const demo = isDemoMode()

  const counts = useServerCounts('clients', demo ? null : clientId, 'leads', STATUS_COUNT_SPECS, reloadKey)

  useEffect(() => {
    if (demo || !clientId) { setRows([]); setComplete(false); return }
    let cancelled = false
    setComplete(false)
    setError(null)
    const col = collection(db, `clients/${clientId}/leads`)
    loadAllPages<DirectoryRow, QueryDocumentSnapshot>(
      async (cursor) => {
        const snap = await getDocs(query(
          col,
          orderBy(documentId()),
          ...(cursor ? [startAfter(cursor)] : []),
          limit(DIRECTORY_CHUNK_SIZE),
        ))
        return {
          rows: snap.docs.map((d) => toDirectoryRow(d.id, d.data(), Date.now(), pastCustomerAfterDays)),
          cursor: snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null,
        }
      },
      DIRECTORY_CHUNK_SIZE,
      (soFar) => { if (!cancelled) setRows(sortDirectoryRows(soFar)) },
      () => cancelled,
    ).then((res) => {
      if (!cancelled) setComplete(res.complete)
    }).catch((err: unknown) => {
      if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)))
    })
    return () => { cancelled = true }
  }, [clientId, reloadKey, demo, pastCustomerAfterDays])

  if (demo) {
    const demoRows = sortDirectoryRows(
      (DEMO_CLIENT_DATA.leads as unknown as Array<Record<string, unknown> & { id: string }>)
        .map((l) => toDirectoryRow(l.id, l, Date.now(), pastCustomerAfterDays)),
    )
    return {
      rows: demoRows,
      complete: true,
      server: { total: demoRows.length, byStatus: {} },
      error: null,
      reload,
    }
  }

  const { total, ...byStatus } = counts
  return { rows, complete, server: { total, byStatus }, error, reload }
}
